import { handleCardCtaClick } from "../../MobileApp/webviewBridge";
import { ScrollableCards } from "../ScrollableCards";
import {
  RatingChip,
  formatDistance,
  formatRelativeDay,
  formatTime12h,
} from "../availability/AvailabilityCard";
import { ArrowRightIcon, MapPinIcon } from "../availability/icons";
import type { OpenPlayFacility, OpenPlaySession } from "./validate";

// Same card structure and classes as the instant-booking court card (photo in
// `.lk-availability-card-media`, then the body); only the tiles differ — a seat
// is priced per player and carries a level, not a surface — and a small
// "OPEN PLAY · SPORT" label sits above the name. The photo is absolutely
// positioned inside its media wrapper, so it must never be a direct child of
// the card (it would cover the body).

/** "$25/player" from cents, whole dollars like the court card's "$60/hr". */
export function formatPricePerPlayer(cents: number): string {
  return `$${Math.ceil(cents / 100)}/player`;
}

function SessionPill({ session }: { session: OpenPlaySession }) {
  return (
    <a
      className="lk-availability-slot"
      href={session.url}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={`${session.level} open play, ${session.when}, ${session.price}, ${session.spotsLeft} spots left`}
      onClick={(e) => handleCardCtaClick(e, session.url)}
    >
      <div className="lk-availability-slot-head">
        <span className="lk-availability-slot-day">{formatRelativeDay(session.date)}</span>
        <span className="lk-availability-slot-time">{formatTime12h(session.startTime)}</span>
      </div>
      <div className="lk-availability-slot-body">
        <span className="lk-availability-slot-price">
          {formatPricePerPlayer(session.pricePerPlayerCents)}
        </span>
        <span className="lk-availability-slot-surface lk-availability-slot-surface--wrap">
          {session.level}
        </span>
      </div>
    </a>
  );
}

/** "OPEN PLAY · PICKLEBALL", or just "OPEN PLAY" when the sessions span sports. */
export function openPlayEyebrow(sessions: readonly Pick<OpenPlaySession, "sport">[]): string {
  const sports = new Set(sessions.map((s) => s.sport.toUpperCase()));
  return sports.size === 1 ? `OPEN PLAY \u00b7 ${[...sports][0]}` : "OPEN PLAY";
}

function OpenPlayCard({ facility }: { facility: OpenPlayFacility }) {
  const miles = facility.distance;

  return (
    <a
      className="lk-availability-card"
      href={facility.url}
      target="_blank"
      rel="noopener noreferrer"
      onClick={(e) => handleCardCtaClick(e, facility.url)}
    >
      <div className="lk-availability-card-media">
        <div
          className="lk-availability-card-image"
          style={facility.photoUrl ? { backgroundImage: `url(${facility.photoUrl})` } : undefined}
          role="img"
          aria-label={facility.name}
        />
        <RatingChip rating={facility.rating} />
      </div>
      <div className="lk-availability-card-body">
        <div className="lk-availability-card-top">
          <div className="lk-availability-card-eyebrow">{openPlayEyebrow(facility.sessions)}</div>
          <div className="lk-availability-card-name">{facility.name}</div>
          <div className="lk-availability-card-location">
            <span className="lk-availability-card-icon">{MapPinIcon}</span>
            <span>
              {facility.city}, {facility.state}
              {typeof miles === "number" ? (
                <>
                  {" - "}
                  <strong>{formatDistance(miles)}</strong>
                </>
              ) : null}
            </span>
          </div>
        </div>
        <div className="lk-availability-card-slots">
          {facility.sessions.slice(0, 2).map((session) => (
            <SessionPill key={session.sessionId} session={session} />
          ))}
        </div>
        <span className="lk-availability-card-cta">
          See more
          <span className="lk-availability-card-arrow">{ArrowRightIcon}</span>
        </span>
      </div>
    </a>
  );
}

export function OpenPlayCards({ facilities }: { facilities: OpenPlayFacility[] }) {
  if (facilities.length === 0) return null;
  return (
    <ScrollableCards className="lk-availability-cards">
      {facilities.map((facility) => (
        <OpenPlayCard key={facility.id} facility={facility} />
      ))}
    </ScrollableCards>
  );
}
